import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { api, type AdminReachSenderAccount } from '@/lib/api'

const STATUS_LABELS: Record<string, string> = {
  warming_up: 'Прогрев',
  active: 'Активен',
  paused: 'Пауза',
  flood_wait: 'Flood wait',
  banned: 'Забанен',
  revoked: 'Отозван',
}

const STATUS_OPTIONS = ['warming_up', 'active', 'paused', 'banned', 'revoked'] as const

function SenderRow({ sender }: { sender: AdminReachSenderAccount }) {
  const queryClient = useQueryClient()
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['admin', 'reach', 'senders'] })

  const [orgId, setOrgId] = useState('')
  const [projectId, setProjectId] = useState('')

  const statusMutation = useMutation({
    mutationFn: (status: string) => api.setAdminReachSenderStatus(sender.id, status),
    onSuccess: invalidate,
  })
  const assignMutation = useMutation({
    mutationFn: () => {
      const org = parseInt(orgId, 10)
      if (!Number.isFinite(org) || org <= 0) throw new Error('Укажите корректный ID организации')
      const project = projectId.trim() === '' ? null : parseInt(projectId, 10)
      if (project !== null && (!Number.isFinite(project) || project <= 0)) {
        throw new Error('Некорректный ID проекта')
      }
      return api.assignAdminReachSender(sender.id, org, project)
    },
    onSuccess: () => {
      setOrgId('')
      setProjectId('')
      invalidate()
    },
  })
  const detachMutation = useMutation({
    mutationFn: (assignmentId: number) => api.detachAdminReachSender(sender.id, assignmentId),
    onSuccess: invalidate,
  })

  const isPlatform = sender.ownership === 'platform'
  const title =
    sender.username
      ? `@${sender.username}`
      : sender.display_name || `#${sender.id}`

  return (
    <div className="admin-sender-card">
      <div className="admin-sender-card__head">
        <div className="admin-sender-card__id">
          <b>{title}</b>
          <span className="admin-sender-card__meta">
            ID {sender.id}
            {sender.phone_masked ? ` · •••${sender.phone_masked}` : ''}
            {sender.telegram_user_id ? ` · TG ${sender.telegram_user_id}` : ''}
          </span>
        </div>
        <div className="admin-sender-card__badges">
          <span className={`admin-badge ${isPlatform ? 'admin-badge--platform' : ''}`}>
            {isPlatform ? 'Платформенный' : 'Организации'}
          </span>
          <span className={`admin-badge admin-badge--status admin-badge--${sender.status}`}>
            {STATUS_LABELS[sender.status] ?? sender.status}
          </span>
          {sender.has_proxy && <span className="admin-badge">прокси</span>}
          {sender.ownership === 'platform' && (
            <span className="admin-sender-card__meta">отправлено сегодня: {sender.daily_sent_count}</span>
          )}
        </div>
      </div>

      {sender.last_error && (
        <div className="admin-sender-card__error">{sender.last_error}</div>
      )}

      <div className="admin-sender-card__row">
        <label>
          Статус
          <select
            value={sender.status}
            disabled={statusMutation.isPending}
            onChange={(e) => statusMutation.mutate(e.target.value)}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s] ?? s}
              </option>
            ))}
          </select>
        </label>
        {sender.status === 'flood_wait' && (
          <span className="admin-sender-card__meta">
            flood_wait управляется системой автоматически
          </span>
        )}
      </div>

      {isPlatform && (
        <div className="admin-sender-card__assign">
          <div className="admin-sender-card__assignments">
            {sender.assignments.filter((a) => a.active).length === 0 && (
              <span className="admin-sender-card__meta">Нет активных привязок</span>
            )}
            {sender.assignments
              .filter((a) => a.active)
              .map((a) => (
                <span key={a.id} className="admin-chip">
                  org {a.organization_id}
                  {a.lead_project_id ? ` · проект ${a.lead_project_id}` : ' · вся организация'}
                  <button
                    type="button"
                    title="Отвязать"
                    disabled={detachMutation.isPending}
                    onClick={() => detachMutation.mutate(a.id)}
                  >
                    ×
                  </button>
                </span>
              ))}
          </div>
          <div className="admin-sender-card__assign-form">
            <label>
              ID организации
              <input
                inputMode="numeric"
                placeholder="3"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
              />
            </label>
            <label>
              ID проекта (необязательно)
              <input
                inputMode="numeric"
                placeholder="124"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="admin-sender-card__assign-btn"
              disabled={assignMutation.isPending}
              onClick={() => assignMutation.mutate()}
            >
              Привязать
            </button>
          </div>
          {assignMutation.isError && (
            <div className="admin-sender-card__error">
              {(assignMutation.error as Error)?.message ?? 'Не удалось привязать'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function AdminSendersPage() {
  const { user } = useAuth()
  const isAdmin = !!user?.is_sensor_admin

  const sendersQuery = useQuery({
    queryKey: ['admin', 'reach', 'senders'],
    queryFn: () => api.getAdminReachSenders(),
    enabled: isAdmin,
  })

  if (!isAdmin) {
    return (
      <div className="admin-senders-page">
        <div className="admin-senders-empty">
          Раздел доступен только администраторам платформы.
        </div>
      </div>
    )
  }

  const senders = sendersQuery.data ?? []
  const platformCount = senders.filter((s) => s.ownership === 'platform').length

  return (
    <div className="admin-senders-page">
      <header className="admin-senders-header">
        <div>
          <p className="reach-workspace__eyebrow">Sensor.Reach · Админ</p>
          <h2>Sender-аккаунты</h2>
          <p className="admin-senders-subtitle">
            Платформенные сервисные аккаунты ({platformCount}) доступны организациям только
            через привязки. Аккаунты организаций управляются их владельцами.
          </p>
        </div>
      </header>

      {sendersQuery.isLoading && <div className="admin-senders-empty">Загрузка…</div>}
      {sendersQuery.isError && (
        <div className="admin-senders-empty">
          Не удалось загрузить список sender-аккаунтов.
        </div>
      )}
      {!sendersQuery.isLoading && !sendersQuery.isError && senders.length === 0 && (
        <div className="admin-senders-empty">Sender-аккаунты ещё не зарегистрированы.</div>
      )}

      <div className="admin-senders-list">
        {senders.map((s) => (
          <SenderRow key={s.id} sender={s} />
        ))}
      </div>
    </div>
  )
}
